/**
 * Provider-agnostic channel category helpers.
 * Adapters supply cached rows; this module never invents category IDs.
 */

export type ChannelCategory = {
  id: string;
  name: string;
  path: string | null;
  parentId: string | null;
  isLeaf?: boolean;
};

export function normalizeCategoryQuery(query: string): string {
  return query.trim().toLowerCase();
}

export function filterChannelCategories(categories: ChannelCategory[], query: string): ChannelCategory[] {
  const q = normalizeCategoryQuery(query);
  if (!q) return categories;
  return categories.filter((c) => {
    const hay = `${c.name} ${c.path ?? ""} ${c.id}`.toLowerCase();
    return hay.includes(q);
  });
}

export function childrenOf(categories: ChannelCategory[], parentId: string | null): ChannelCategory[] {
  return categories.filter((c) => (c.parentId ?? null) === parentId);
}

function tokensFromHints(hints: string[]): string[] {
  const out: string[] = [];
  for (const hint of hints) {
    for (const part of hint.toLowerCase().split(/[^a-z0-9]+/)) {
      if (part.length >= 3 && !out.includes(part)) out.push(part);
    }
  }
  return out;
}

/**
 * Rank cached categories that share tokens with canonical product data.
 * Returns [] when there is no cache or no overlap — never fabricates rows.
 */
export function suggestChannelCategories(
  hints: string[],
  categories: ChannelCategory[],
  limit = 5,
): ChannelCategory[] {
  if (categories.length === 0) return [];
  const tokens = tokensFromHints(hints);
  if (tokens.length === 0) return [];
  const scored = categories
    .map((c) => {
      const hay = `${c.name} ${c.path ?? ""}`.toLowerCase();
      let score = 0;
      for (const t of tokens) {
        if (hay.includes(t)) score += t.length;
      }
      return { c, score };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score);
  const seen = new Set<string>();
  const out: ChannelCategory[] = [];
  for (const row of scored) {
    if (seen.has(row.c.id)) continue;
    seen.add(row.c.id);
    out.push(row.c);
    if (out.length >= limit) break;
  }
  return out;
}

export function categoryHierarchyLabel(category: ChannelCategory): string {
  if (category.path && category.path.trim()) return category.path;
  return category.name;
}
